/**
 * BootLoader.js
 *
 * The app's cold-start / Suspense loading screen: brand-orange gradient, a white
 * logo plate with a soft pulsing ring, the loading message and a slim indeterminate
 * progress bar. Deliberately self-contained — it renders BEFORE the providers
 * (theme, i18n context, redux) mount, so it takes everything through props and
 * uses only core React-Native + expo-linear-gradient. Pure native-driver
 * animations (transform / opacity) keep it smooth while the JS thread is busy
 * booting.
 *
 * @module components/BootLoader
 */

import React, {useEffect, useRef} from 'react';
import {Animated, Easing, Image, StyleSheet, Text, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {BRAND} from '../helpers/managementTheme';

const LOGO = require('../../assets/logo-small.png');

/** Width of the progress track / travelling bar (px). */
const TRACK_WIDTH = 132;
const BAR_WIDTH = 52;

/**
 * Premium boot / suspense loading screen.
 *
 * @param {object} props
 * @param {string} [props.message] Loading text under the logo (already translated).
 * @returns {JSX.Element} The full-screen loader.
 */
const BootLoader = ({message}) => {
    const pulse = useRef(new Animated.Value(0)).current;
    const travel = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const pulseLoop = Animated.loop(
            Animated.timing(pulse, {toValue: 1, duration: 1700, easing: Easing.out(Easing.quad), useNativeDriver: true}),
        );
        const travelLoop = Animated.loop(
            Animated.timing(travel, {toValue: 1, duration: 1250, easing: Easing.inOut(Easing.cubic), useNativeDriver: true}),
        );

        pulseLoop.start();
        travelLoop.start();

        return () => {
            pulseLoop.stop();
            travelLoop.stop();
        };
    }, [pulse, travel]);

    const ringStyle = {
        opacity: pulse.interpolate({inputRange: [0, 0.15, 1], outputRange: [0, 0.5, 0]}),
        transform: [{scale: pulse.interpolate({inputRange: [0, 1], outputRange: [1, 1.75]})}],
    };
    const barStyle = {
        transform: [{translateX: travel.interpolate({inputRange: [0, 1], outputRange: [-BAR_WIDTH, TRACK_WIDTH]})}],
    };

    return (
        <LinearGradient colors={BRAND.gradient} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.root}>
            <View pointerEvents="none" style={styles.blobA} />
            <View pointerEvents="none" style={styles.blobB} />

            <View style={styles.center}>
                <View style={styles.plateWrap}>
                    <Animated.View pointerEvents="none" style={[styles.ring, ringStyle]} />
                    <View style={styles.plate}>
                        <Image source={LOGO} style={styles.logo} resizeMode="contain" />
                    </View>
                </View>

                {!!message && <Text style={styles.message} numberOfLines={2}>{message}</Text>}

                <View style={styles.track}>
                    <Animated.View style={[styles.bar, barStyle]} />
                </View>
            </View>
        </LinearGradient>
    );
};

const styles = StyleSheet.create({
    root: {flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: BRAND.base, overflow: 'hidden'},
    blobA: {position: 'absolute', width: 420, height: 420, borderRadius: 210, backgroundColor: 'rgba(255,255,255,0.12)', top: -170, right: -150},
    blobB: {position: 'absolute', width: 320, height: 320, borderRadius: 160, backgroundColor: 'rgba(255,255,255,0.09)', bottom: -130, left: -110},
    center: {alignItems: 'center'},
    plateWrap: {width: 112, height: 112, alignItems: 'center', justifyContent: 'center'},
    ring: {position: 'absolute', width: 112, height: 112, borderRadius: 56, borderWidth: 2, borderColor: '#FFFFFF'},
    plate: {
        width: 112,
        height: 112,
        borderRadius: 36,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#7C2D12',
        shadowOffset: {width: 0, height: 14},
        shadowOpacity: 0.3,
        shadowRadius: 22,
        elevation: 10,
    },
    logo: {width: 68, height: 68},
    message: {color: 'rgba(255,255,255,0.95)', fontSize: 15, fontWeight: '700', letterSpacing: 0.3, marginTop: 30, textAlign: 'center', paddingHorizontal: 32},
    track: {width: TRACK_WIDTH, height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.28)', overflow: 'hidden', marginTop: 18},
    bar: {width: BAR_WIDTH, height: 4, borderRadius: 2, backgroundColor: '#FFFFFF'},
});

export default React.memo(BootLoader);
